/**
 * PostsListPage
 *
 * Displays all posts with status badges (Draft / Ready to publish).
 * Includes dropdown filter to focus on specific status.
 */

import { useState, useMemo, useReducer, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { PostWriterSubmenuMemo } from '../components/post-writer/PostWriterSubmenu';
import { PostsPageHeaderMemo } from '../components/posts/PostsPageHeader';
import { PostToolbarMemo } from '../components/posts/PostToolbar';
import { PostListMemo } from '../components/posts/PostList';
import { Pagination } from '../components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Loader2, FileText } from 'lucide-react';
import type { IPostListItem, IPostFilterTab, IPostSortOption, IFilterTab } from '../@types/posts';
import type { Id } from '../../convex/_generated/dataModel';

const PAGE_SIZE = 10;

type StatusFilter = 'all' | 'draft' | 'ready';

type PostFilterState = {
  searchQuery: string;
  activeTab: IPostFilterTab;
  sortOption: IPostSortOption;
  currentPage: number;
  statusFilter: StatusFilter;
};

type PostFilterAction =
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_TAB'; payload: IPostFilterTab }
  | { type: 'SET_SORT'; payload: IPostSortOption }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_STATUS_FILTER'; payload: StatusFilter };

const initialFilterState: PostFilterState = {
  searchQuery: '',
  activeTab: 'all',
  sortOption: 'newest',
  currentPage: 1,
  statusFilter: 'all',
};

function filterReducer(state: PostFilterState, action: PostFilterAction): PostFilterState {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.payload, currentPage: 1 };
    case 'SET_TAB':
      return { ...state, activeTab: action.payload, currentPage: 1 };
    case 'SET_SORT':
      return { ...state, sortOption: action.payload, currentPage: 1 };
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload };
    case 'SET_STATUS_FILTER':
      return { ...state, statusFilter: action.payload, currentPage: 1 };
  }
}

export const PostsListPage = () => {
  const navigate = useNavigate();

  // Fetch all posts (omitting hasImage returns all with readyStatus)
  const posts = useQuery(api.posts.getPostsByImageStatus, {}) as
    | (IPostListItem & { readyStatus: 'draft' | 'ready'; imageUrl: string | null })[]
    | undefined;

  // UI State
  const [filters, dispatchFilter] = useReducer(filterReducer, initialFilterState);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Loading/empty states
  const isLoading = posts === undefined;
  const isEmpty = posts?.length === 0;

  // Filter by status first
  const statusFilteredPosts = useMemo(() => {
    if (!posts) return [];
    if (filters.statusFilter === 'all') return posts;
    return posts.filter(p => p.readyStatus === filters.statusFilter);
  }, [posts, filters.statusFilter]);

  // Tab counts
  const tabCounts = useMemo(() => {
    return {
      all: statusFilteredPosts.length,
      needs_image: statusFilteredPosts.filter(p => p.status === 'completed' && !p.hasGeneratedImage)
        .length,
      generating: statusFilteredPosts.filter(
        p => p.status === 'generating' || p.status === 'pending'
      ).length,
    };
  }, [statusFilteredPosts]);

  // Tabs config
  const tabs: IFilterTab[] = useMemo(
    () => [
      { id: 'all', label: 'All', count: tabCounts.all },
      { id: 'needs_image', label: 'Needs Image', count: tabCounts.needs_image },
      { id: 'generating', label: 'Generating', count: tabCounts.generating },
    ],
    [tabCounts]
  );

  // Filtered and sorted posts
  const filteredPosts = useMemo(() => {
    let result = [...statusFilteredPosts];

    // Filter by tab
    if (filters.activeTab === 'needs_image') {
      result = result.filter(p => p.status === 'completed' && !p.hasGeneratedImage);
    } else if (filters.activeTab === 'generating') {
      result = result.filter(p => p.status === 'generating' || p.status === 'pending');
    }

    // Filter by search
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(
        p => p.postTitle?.toLowerCase().includes(query) || p.hook?.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      if (filters.sortOption === 'newest') return b.generatedAt - a.generatedAt;
      if (filters.sortOption === 'oldest') return a.generatedAt - b.generatedAt;
      return (a.postTitle || '').localeCompare(b.postTitle || '');
    });

    return result;
  }, [statusFilteredPosts, filters.activeTab, filters.searchQuery, filters.sortOption]);

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / PAGE_SIZE);
  const paginatedPosts = useMemo(() => {
    const start = (filters.currentPage - 1) * PAGE_SIZE;
    return filteredPosts.slice(start, start + PAGE_SIZE);
  }, [filteredPosts, filters.currentPage]);

  // Filter change handlers
  const handleTabChange = useCallback((tab: IPostFilterTab) => {
    dispatchFilter({ type: 'SET_TAB', payload: tab });
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    dispatchFilter({ type: 'SET_SEARCH', payload: query });
  }, []);

  const handleSortChange = useCallback((sort: IPostSortOption) => {
    dispatchFilter({ type: 'SET_SORT', payload: sort });
  }, []);

  // Selection handlers
  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedIds(new Set(paginatedPosts.map(p => p._id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [paginatedPosts]
  );

  const handleSelectPost = useCallback((id: Id<'posts'>, selected: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  // Action handlers
  const handlePostClick = useCallback(
    (id: Id<'posts'>) => {
      navigate(`/post/${id}`);
    },
    [navigate]
  );

  const handleEditPost = useCallback(
    (id: Id<'posts'>) => {
      navigate(`/post/${id}/edit`);
    },
    [navigate]
  );

  const handleDeletePost = useCallback((_id: Id<'posts'>) => {
    // TODO: Implement delete confirmation modal
  }, []);

  const handleCancelGeneration = useCallback((_id: Id<'posts'>) => {
    // TODO: Implement cancel generation
  }, []);

  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent: (
      <div className="flex h-full flex-col bg-background">
        <div className="flex flex-1 flex-col gap-6 px-10 py-8">
          {/* Page Header with Status Filter */}
          <div className="flex items-start justify-between">
            <PostsPageHeaderMemo
              title="Posts"
              count={posts?.length ?? 0}
              subtitle="All your generated posts"
            />
            <Select
              value={filters.statusFilter}
              onValueChange={(value: StatusFilter) =>
                dispatchFilter({ type: 'SET_STATUS_FILTER', payload: value })
              }
            >
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="ready">Ready to publish</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Toolbar */}
          {!isLoading && !isEmpty && (
            <PostToolbarMemo
              tabs={tabs}
              activeTab={filters.activeTab}
              onTabChange={handleTabChange}
              searchQuery={filters.searchQuery}
              onSearchChange={handleSearchChange}
              sortOption={filters.sortOption}
              onSortChange={handleSortChange}
            />
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && isEmpty && (
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <FileText className="mb-4 h-12 w-12 text-foreground-placeholder" />
              <h3 className="mb-1 text-lg font-medium text-foreground">No posts yet</h3>
              <p className="text-sm text-foreground-muted">
                Generate posts from topics to see them here.
              </p>
            </div>
          )}

          {/* No Results State */}
          {!isLoading && !isEmpty && filteredPosts.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <FileText className="mb-4 h-12 w-12 text-foreground-placeholder" />
              <h3 className="mb-1 text-lg font-medium text-foreground">No matching posts</h3>
              <p className="text-sm text-foreground-muted">Try adjusting your search or filters.</p>
            </div>
          )}

          {/* Post List */}
          {!isLoading && !isEmpty && filteredPosts.length > 0 && (
            <>
              <PostListMemo
                posts={paginatedPosts}
                selectedIds={selectedIds}
                onSelectAll={handleSelectAll}
                onSelectPost={handleSelectPost}
                onPostClick={handlePostClick}
                onEditPost={handleEditPost}
                onDeletePost={handleDeletePost}
                onCancelGeneration={handleCancelGeneration}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={filters.currentPage}
                  totalPages={totalPages}
                  totalItems={filteredPosts.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={(page: number) =>
                    dispatchFilter({ type: 'SET_PAGE', payload: page })
                  }
                />
              )}
            </>
          )}
        </div>
      </div>
    ),
  };
};
